import type { Country } from "react-phone-number-input";
import type { Market, MarketQuote, Money } from "@/lib/api/checkout.schema";
import { MOBILE_MONEY_COUNTRIES, phoneCountry } from "@/lib/phone";

type Markets = readonly Market[] | null | undefined;

/** Markets as served, with country codes normalised to upper case. */
export function normaliseMarkets(markets: Markets): Market[] {
  return (markets ?? []).map((m) => ({ ...m, country: m.country.toUpperCase() }));
}

/**
 * Countries whose mobile-money numbers may pay. Driven by the API's
 * `markets` so ops can switch a market on without a frontend deploy; the
 * constant is only a fallback for responses without the field.
 */
export function marketCountries(markets: Markets): readonly Country[] {
  const fromApi = normaliseMarkets(markets).map((m) => m.country as Country);
  return fromApi.length > 0 ? fromApi : MOBILE_MONEY_COUNTRIES;
}

export function findMarket(markets: Markets, country: string | null | undefined): Market | null {
  if (!country) return null;
  const code = country.toUpperCase();
  return normaliseMarkets(markets).find((m) => m.country === code) ?? null;
}

/** The market a payer's phone number belongs to, if served. */
export function marketForPhone(markets: Markets, phone: string): Market | null {
  return findMarket(markets, phoneCountry(phone));
}

/** The merchant's own market: the one charged in the price currency. */
export function domesticMarket(markets: Markets, priceCurrency: string): Market | null {
  const all = normaliseMarkets(markets);
  return all.find((m) => m.currency === priceCurrency) ?? all[0] ?? null;
}

/**
 * Indicative conversion for previewing prices in a market's currency,
 * implied by a quote (charge / price). Null until a quote with a charge
 * exists. Purely a preview: the checkout response is the binding figure.
 */
export interface MarketPreview {
  market: MarketQuote["market"];
  rate: number;
}

export function previewFromQuote(quote: MarketQuote | undefined | null): MarketPreview | null {
  if (!quote?.charge || quote.price.value <= 0) return null;
  return { market: quote.market, rate: quote.charge.value / quote.price.value };
}

/** True when a price sits under the market's cross-border floor. */
export function isBelowMarketMinimum(market: Market | null | undefined, price: Money): boolean {
  const min = market?.min_price;
  return !!min && min.currency === price.currency && price.value < min.value;
}

/** Charge to show the payer when it differs from the merchant's price currency. */
export function foreignCharge(quote: MarketQuote | undefined | null): Money | null {
  if (!quote?.payable || !quote.charge) return null;
  return quote.charge.currency !== quote.price.currency ? quote.charge : null;
}
